import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import { ICardElementWithChildren } from "backend/model/model";
import React from "react";
import { formatPerformancePercentage } from "utils/utils";
import HighChart from "./Highchart";
import { IChartSelectEvent } from "./types";

const usePerformanceDonutStyles = makeThemedStyles(
  "PerformanceDonut",
  (theme) => ({
    container: {
      display: "flex",
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
    },
    chartTitleStyle: {
      fontFamily: "Roboto, Helvetica",
      fontSize: "18px",
      fontWeight: 500,
      color: "rgb(84,84,84)",
    },
  })
);

export const PerformanceDonut = React.memo(
  (
    props: React.PropsWithChildren<{
      element: ICardElementWithChildren;
      valueKey: keyof ICardElementWithChildren;
      colorKey: keyof ICardElementWithChildren;
      prettyKey: keyof ICardElementWithChildren;
      title?: string;
      angleStart?: number;
      angleEnd?: number;
      width?: number;
      height?: string | number;
      onSelect?: (value: IChartSelectEvent) => void;
      disableAnimation?: boolean;
      neutralcolor?: boolean;
    }>
  ) => {
    const styles = usePerformanceDonutStyles();
    const { element: el } = props;

    const valueKey: keyof ICardElementWithChildren = props.valueKey || "PERF";
    const prettyKey: keyof ICardElementWithChildren =
      props.prettyKey || "PERF_prettyvalue";

    const startAngle = props.angleStart || 0;
    const endAngle = props.angleEnd || 360;

    const hcOptions = React.useMemo(() => {
      if (!el) {
        return {};
      }
      return {
        credits: {
          enabled: false,
        },
        chart: {
          width: props.width || 120,
          type: "solidgauge",
          height: props.height || "110%",
          backgroundColor: "rgba(255,255,255,0)",
        },
        title: {
          useHTML: true,
          verticalAlign: "middle",
          floating: true,
          text: `<div class="${styles.container}"><div class="${
            styles.chartTitleStyle
          }">${formatPerformancePercentage(el[valueKey])}</div></div>`,
        },
        pane: {
          startAngle: startAngle,
          endAngle: endAngle,
          background: [
            {
              outerRadius: "112%",
              innerRadius: "83%",
              backgroundColor: `#${
                props.neutralcolor
                  ? "b3b3b3"
                  : el?.[props?.colorKey || "rangecolor"] || "333333"
              }22`,
              borderWidth: 0,
              shape: "arc",
            },
          ],
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: false,
            },
            animation: !props.disableAnimation,
            linecap: "round",
            stickyTracking: false,
            rounded: false,
          },
        },
        tooltip: {
          useHTML: true,
          formatter: function () {
            return `${props.title || "Actual"} ${el[prettyKey]}`;
          },
        },
        series: [
          {
            name: props.title || "Actual",
            events: {
              click: function (this: any, event: any) {
                if (props.onSelect) {
                  props.onSelect({
                    category: event.point.category,
                    index: this.index,
                    name: this.name,
                    items: [props.element],
                  });
                }
              },
            },
            data: [
              {
                color: {
                  linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                  stops: [
                    ...(el[valueKey]
                      ? [
                          [
                            0,
                            `#${
                              props.neutralcolor
                                ? "b3b3b3"
                                : el.rangecolor || "gray"
                            }ff`,
                          ],
                          [
                            1,
                            `#${
                              props.neutralcolor
                                ? "b3b3b3"
                                : el.rangecolor || "gray"
                            }99`,
                          ],
                        ]
                      : [
                          [0, `#${el.rangecolor || "gray"}00`],
                          [1, `#${el.rangecolor || "gray"}00`],
                        ]),
                  ],
                },
                radius: "112%",
                innerRadius: "83%",
                y: Number(el[valueKey] || 0),
              },
            ],
          },
        ],
      };
    }, [el]);

    if (!el) return null;
    return <HighChart chartOptions={hcOptions} />;
  }
);
